import { Button, ButtonLevel, ButtonSize, Menu, MenuItem, MenuPopover, MenuSection, MenuSeparator } from '@vizit/atoms';
import { MenuTrigger, RadialProgress, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { AlertCircleIcon, ChevronDownIcon, PlusIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type { Key } from 'react';
import { Header } from 'react-aria-components';
import { ProductCategoryColorSquare } from './product-category-color-square';
import { describeCategoryPath, findSharedPathIds, getProductCategoryDetails } from './product-category-names';
import { findIndistinctCategoryIds, sortPickerCategories } from './product-category-names';
import type { PickerPathsStatus, PickerProductCategory } from './types';

/** Keys of the picker's fixed rows; category IDs are UUIDs, so they never collide. */
const NONE_KEY = 'none';
const ADD_KEY = 'add';

export interface ProductCategoryPickerProps {
  /**
   * Categories the benchmark or collection is scored under; the menu lists them by name, whatever their order
   */
  scoredCategories: PickerProductCategory[];
  /**
   * Categories still being scored; listed after the scored ones and not selectable
   *
   * @optional
   */
  processingCategories?: PickerProductCategory[];
  /**
   * Categories whose scoring failed; listed after the processing ones, selectable, marked "Failed"
   *
   * @optional
   */
  failedCategories?: PickerProductCategory[];
  /**
   * Where the categories' paths stand. While they load, a row without its path keeps a line's room for it, so the rows
   * do not grow when the paths arrive; when they failed, that line says so, rather than the row look like it has none
   *
   * @optional
   * @default 'ready'
   */
  pathsStatus?: PickerPathsStatus;
  /**
   * The selected category, or `null` for "None selected"
   */
  selectedCategoryId: string | null;
  /**
   * Called with the picked category, or `null` when "None selected" is picked
   */
  onSelectionChange: (categoryId: string | null) => void;
  /**
   * Called from "+ Add product category". Leave unset where adding is not offered (e.g. the Comparison), which hides
   * the row and its divider
   *
   * @optional
   */
  onAddCategory?: () => void;
  /**
   * Whether "None selected" is offered. Surfaces that always score under a category pass `false`
   *
   * @optional
   * @default true
   */
  allowNone?: boolean;
  /**
   * Whether the trigger is disabled
   *
   * @optional
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the menu starts open
   *
   * @optional
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * How the trigger looks: a compact button, or the 190px input-like field the scoring bars put in their measure
   * group
   *
   * @optional
   * @default 'button'
   */
  appearance?: 'button' | 'field';
  /**
   * Additional CSS classes for the trigger button
   *
   * @optional
   */
  className?: string;
}

/**
 * Picks the product category whose scores a grid shows. A standalone control, so a toolbar can place it and the
 * Side-by-Side Comparison can reuse it: the trigger shows the current category, and the menu lists "None selected"
 * (unless `allowNone` is off), the scored categories with a check on the current one, the categories still scoring
 * as disabled rows, the ones whose scoring failed (selectable, so their partial scores can be seen) and, when
 * `onAddCategory` is given, a divider and "+ Add product category". Each group is listed by name, so a row stays put
 * when it is picked, and every category row shows its parents under its name, then its retailer only when that alone
 * tells it apart from another row. Picking a
 * scored category is instant; the caller decides what loading means.
 */
export function ProductCategoryPicker(props: ProductCategoryPickerProps) {
  const {
    scoredCategories,
    processingCategories = [],
    failedCategories = [],
    pathsStatus = 'ready',
    selectedCategoryId,
    onSelectionChange,
    onAddCategory,
    allowNone = true,
    isDisabled = false,
    defaultOpen = false,
    appearance = 'button',
    className,
  } = props;
  const isField = appearance === 'field';

  const scoredRows = sortPickerCategories(scoredCategories);
  const processingRows = sortPickerCategories(processingCategories);
  const failedRows = sortPickerCategories(failedCategories);
  const listed = [...scoredRows, ...processingRows, ...failedRows];
  // A row names its retailer only when that alone tells it apart from another row, as the trigger does.
  const sharedPathIds = findSharedPathIds(listed);
  // What a row's name ends with after its path, so the check below compares the names rows are given.
  const stateById = new Map([
    ...processingRows.map(({ id }) => [id, 'scoring'] as const),
    ...failedRows.map(({ id }) => [id, 'failed'] as const),
  ]);
  const describe = (category: PickerProductCategory) =>
    describeCategoryPath(category, { state: stateById.get(category.id), withRetailer: sharedPathIds.has(category.id) });
  // Rows a screen reader would name the same (the same category listed twice, or a parent named like a retailer) add
  // their ID to their accessible name, as the add dialog's rows do. On screen they read as before.
  const indistinctIds = findIndistinctCategoryIds(listed.map((category) => ({ category, ...describe(category) })));
  // The trigger has room for a name only, so it adds what tells a repeated name apart, as the add dialog does.
  const detailsById = getProductCategoryDetails(listed, sharedPathIds);
  const detailsOf = (category: PickerProductCategory) => detailsById.get(category.id) ?? [];
  // A category just added is selected while it scores, so the trigger names it even though its row is disabled.
  const selected = listed.find((category) => category.id === selectedCategoryId) ?? null;
  const selectedLabel = selected ? [selected.name, ...detailsOf(selected)].join(', ') : 'None selected';

  /**
   * A row's text, for typeahead; its accessible name, which carries the full path, and the ID when it needs one, when
   * it has them (otherwise its content names it); and its name with the path below, both wrapping rather than cut off
   * so the menu never scrolls sideways. A single-line row keeps the menu's row height.
   */
  const describeRow = (category: PickerProductCategory, nameClassName?: string) => {
    const { path, label } = describe(category);
    const idNote = indistinctIds.has(category.id) ? `ID ${category.id}` : '';
    const text = [label, idNote].filter(Boolean).join(', ');
    return {
      text,
      label: path || idNote ? text : undefined,
      name: (
        <span className={cx('flex min-w-0 flex-1 flex-col break-words', nameClassName)}>
          <span>{category.name}</span>
          {path ? (
            <Text elementType="span" size={TextSize.Xs} color={TextColor.Tertiary} showEllipsis={false}>
              {path}
            </Text>
          ) : pathsStatus === 'loading' ? (
            <Skeleton elementType="span" variant="square" className="w-3/4 rounded-sm" aria-hidden>
              <Text elementType="span" size={TextSize.Xs} showEllipsis={false}>
                &nbsp;
              </Text>
            </Skeleton>
          ) : (
            pathsStatus === 'error' && (
              <Text elementType="span" size={TextSize.Xs} color={TextColor.Tertiary} showEllipsis={false}>
                Couldn't load its path
              </Text>
            )
          )}
        </span>
      ),
    };
  };

  const handleAction = (key: Key) => {
    if (key === ADD_KEY) {
      onAddCategory?.();
    } else if (key === NONE_KEY) {
      onSelectionChange(null);
    } else {
      onSelectionChange(String(key));
    }
  };

  return (
    <MenuTrigger defaultOpen={defaultOpen}>
      <Button
        aria-label={`Product category: ${selectedLabel}`}
        level={ButtonLevel.Secondary}
        size={isField ? ButtonSize.Md : ButtonSize.Sm}
        iconStart={
          selected &&
          (isField ? (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center">
              <ProductCategoryColorSquare color={selected.color} />
            </span>
          ) : (
            <ProductCategoryColorSquare color={selected.color} />
          ))
        }
        iconEnd={<ChevronDownIcon size={14} aria-hidden />}
        isDisabled={isDisabled}
        className={cx(
          isField
            ? 'w-[190px] gap-4 px-6 font-medium text-md text-primary [&>svg]:text-gray-utility-400'
            : 'max-w-[260px]',
          className,
        )}
      >
        <span className="flex-1 truncate text-left">
          {selected?.name ?? 'None selected'}
          {/* The selected row's details, so a repeated name reads apart without opening the menu. */}
          {selected && detailsOf(selected).length > 0 && (
            <span className="text-tertiary"> · {detailsOf(selected).join(' · ')}</span>
          )}
        </span>
      </Button>
      <MenuPopover placement="bottom start">
        <Menu
          aria-label="Scored categories"
          selectionMode="single"
          selectedKeys={[selectedCategoryId ?? NONE_KEY]}
          disabledKeys={processingCategories.map((category) => category.id)}
          onAction={handleAction}
          className="min-w-[260px] max-w-[320px]"
        >
          <MenuSection>
            <Header className="px-8 pt-2 pb-3">
              <Text
                size={TextSize.Xs}
                weight={TextWeight.Medium}
                color={TextColor.Tertiary}
                className="font-mono uppercase"
              >
                Scored categories
              </Text>
            </Header>
            {allowNone && (
              <MenuItem id={NONE_KEY} textValue="None selected">
                <ProductCategoryColorSquare color="transparent" />
                None selected
              </MenuItem>
            )}
            {scoredRows.map((category) => {
              const row = describeRow(category, 'pr-10');
              return (
                <MenuItem
                  key={category.id}
                  id={category.id}
                  textValue={row.text}
                  aria-label={row.label}
                  className="h-auto min-h-18"
                >
                  <ProductCategoryColorSquare color={category.color} />
                  {row.name}
                </MenuItem>
              );
            })}
            {processingRows.map((category) => {
              const row = describeRow(category);
              return (
                <MenuItem
                  key={category.id}
                  id={category.id}
                  textValue={row.text}
                  aria-label={row.label}
                  className="h-auto min-h-18"
                >
                  <RadialProgress isIndeterminate size={8} aria-label="Scoring" aria-hidden />
                  {row.name}
                  {/* Room for the check and a gap, since a category just added is selected while it scores. */}
                  <span className="ml-auto shrink-0 pr-12 text-xs">Scoring…</span>
                </MenuItem>
              );
            })}
            {failedRows.map((category) => {
              const row = describeRow(category);
              return (
                <MenuItem
                  key={category.id}
                  id={category.id}
                  textValue={row.text}
                  aria-label={row.label}
                  className="h-auto min-h-18"
                >
                  <AlertCircleIcon size={14} className="shrink-0 text-error-600" aria-hidden />
                  {row.name}
                  <span className="ml-auto shrink-0 pr-12 text-xs text-error-600">Failed</span>
                </MenuItem>
              );
            })}
          </MenuSection>
          {onAddCategory && (
            <>
              <MenuSeparator />
              {/* A command, not a choice: its own unselectable section makes it a plain menu item for assistive tech. */}
              <MenuSection selectionMode="none">
                <MenuItem id={ADD_KEY} textValue="Add product category">
                  <PlusIcon size={14} aria-hidden />
                  Add product category
                </MenuItem>
              </MenuSection>
            </>
          )}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}
